import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { StyledAnchor } from "@/components/common/styles";
import {
  marketingHref,
  newTabProps,
} from "@/components/marketing/marketingLinks";
import { BODY, BODY_SMALL } from "@/components/marketing/marketingType";
import {
  ACCENT,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import { BoldRuns, LandingImage, LandingSection } from "../LandingPrimitives";
import { CardPanel } from "./cardsPanel";
import { BandHeading, type Cards } from "./cardsShared";
import { sectionPad } from "./landingRun";

/** `dot_pricing`: a plain grey dot, not the site's check glyph. */
function DotLine({ label }: { label: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Box
        sx={{
          width: "6.4px",
          height: "6.4px",
          borderRadius: "1440px",
          backgroundColor: "rgba(79, 92, 101, 0.77)",
        }}
      />
      <Box
        sx={{
          fontSize: 14,
          lineHeight: "22px",
          opacity: 0.85,
          color: marketingPalette.ink,
        }}
      >
        <BoldRuns text={label} />
      </Box>
    </Box>
  );
}

/** `hero-product-btn_wrap`: the price and the buy control, one row at the foot. */
function PriceRow({ card }: { card: Card }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [TINY]: {
          flexDirection: "column",
          alignItems: "stretch",
          gap: "8px",
          textAlign: "center",
        },
      }}
    >
      {/* `.hero-product-btn_list` sets itself left, so on a phone the price
          stays at the start of the row the control centres in. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2.08px",
          textAlign: "left",
        }}
      >
        {card.priceLead ? (
          <Box sx={{ fontSize: 12.96, lineHeight: "18.144px" }}>
            {card.priceLead}
          </Box>
        ) : null}
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "4.8px" }}>
          <Box
            sx={{ fontSize: 14.08, fontWeight: 500, lineHeight: "16.896px" }}
          >
            {card.price}
          </Box>
          {card.per ? (
            <Box sx={{ fontSize: 12.96, lineHeight: "18.144px" }}>
              {card.per}
            </Box>
          ) : null}
        </Box>
      </Box>
      {card.cta ? (
        <StyledAnchor
          href={marketingHref(card.cta.href ?? "")}
          {...newTabProps(card.cta)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: "7.04px",
            px: "10.08px",
            borderRadius: "8px",
            backgroundColor: ACCENT,
            color: "common.white",
            fontSize: 14.08,
            lineHeight: "21.12px",
            whiteSpace: "nowrap",
          }}
        >
          {card.cta.label}
        </StyledAnchor>
      ) : null}
    </Box>
  );
}

/** `loc-prosuct_item`: a grid, so its rows share the leftover height evenly. */
function PremiumCard({ card }: { card: Card }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: "20px",
        p: "24px",
        borderRadius: "16px",
        border: `1px solid ${marketingPalette.outline}`,
        backgroundColor: "common.white",
        [TINY]: { p: "20px", gap: "16px" },
      }}
    >
      {card.icon ? (
        <LandingImage src={card.icon} sx={{ width: "44px", height: "44px" }} />
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "4.8px",
        }}
      >
        {/* `body_new.is-medium`: the body ladder at medium weight. */}
        <Typography component="h6" sx={[BODY, { fontWeight: 500 }]}>
          {card.title}
        </Typography>
        {card.body ? (
          // `.hero-product_body`, which wraps "pretty".
          <Typography
            sx={{
              fontSize: 14.08,
              lineHeight: "21.12px",
              opacity: 0.8,
              textWrap: "pretty",
            }}
          >
            {card.body}
          </Typography>
        ) : null}
      </Box>
      {card.bullets?.length ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {card.bullets.map((bullet, index) => (
            <DotLine key={index} label={bullet} />
          ))}
        </Box>
      ) : null}
      {/* `line_divider`: the published rule, which is not a whole pixel. */}
      <Box
        sx={{
          height: "0.953125px",
          backgroundColor: marketingPalette.outline,
        }}
      />
      <PriceRow card={card} />
    </Box>
  );
}

/**
 * `product-card_item.is-extension`: the card the extension page's tinted well
 * carries. The one card on the site that answers the pointer, and it moves as
 * well as tints.
 */
const EXTENSION_CARD = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  alignItems: "center",
  justifyContent: "center",
  p: "24px",
  borderRadius: "16px",
  border: `1px solid ${marketingPalette.wash}`,
  backgroundColor: "common.white",
  boxShadow: "rgb(228, 233, 236) 0px 20px 34px -10px",
  transition: "all 200ms ease",
  [TINY]: { borderColor: "rgba(79, 92, 101, 0.32)" },
  // A pseudo-class outranks the step above it, so the hover holds at every width.
  "&:hover": {
    backgroundColor: "#E6F7F3",
    borderColor: "rgba(26, 179, 148, 0.67)",
    transform: "translate(-8px, -4px)",
  },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
} satisfies SxProps<Theme>;

function ExtensionCard({ card }: { card: Card }) {
  return (
    <StyledAnchor
      href={marketingHref(card.href ?? "")}
      {...newTabProps(card)}
      sx={EXTENSION_CARD}
    >
      {card.icon ? (
        <LandingImage
          src={card.icon}
          sx={{ width: "50.08px", height: "50.08px" }}
        />
      ) : null}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography
          component="h6"
          sx={{
            color: marketingPalette.ink,
            fontSize: 18,
            fontWeight: 500,
            lineHeight: "21.6px",
          }}
        >
          {card.title}
        </Typography>
        {card.body ? (
          <Typography sx={BODY_SMALL}>{card.body}</Typography>
        ) : null}
      </Box>
    </StyledAnchor>
  );
}

/** `loc-resi_wrap`: the extension page sets its product row on a tinted well. */
function ExtensionPanel({ block }: { block: Cards }) {
  return (
    <LandingSection padding="both">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          p: "40px",
          borderRadius: "32px",
          backgroundColor: marketingPalette.surface,
          overflow: "hidden",
          [TINY]: { py: "24px", px: "16px", borderRadius: "16px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            alignItems: "center",
          }}
        >
          {/* `.section_title-pr` in `.loc-resi_wrap`: the pair is centred as a
              box, and a heading that wraps keeps its lines left. */}
          <BandHeading block={block} gap="8px" lines="left" />
          <Box
            sx={{
              display: "grid",
              width: 1,
              gap: "16px",
              gridTemplateColumns: `repeat(${Math.min(block.items.length, 3)}, minmax(0, 1fr))`,
              [NOT_WIDE]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
              [TINY]: { gridTemplateColumns: "1fr" },
            }}
          >
            {block.items.map((card, index) => (
              <ExtensionCard key={index} card={card} />
            ))}
          </Box>
        </Box>
      </Box>
    </LandingSection>
  );
}

/**
 * The bands the published site draws from a product record: a row of priced
 * plan cards, the campaign pages' priced cards on a gradient panel, and the
 * extension page's well of links to them.
 */
export default function CardsProduct({
  block,
  continues,
}: {
  block: Cards;
  continues: boolean;
}) {
  if (!block.items.some((card) => card.price)) {
    return <ExtensionPanel block={block} />;
  }
  if (block.cardPanel) {
    return (
      <LandingSection padding={block.pad ? sectionPad(block.pad) : "break"}>
        <CardPanel block={block} />
      </LandingSection>
    );
  }
  const top = block.pad === "top";
  return (
    <LandingSection
      padding={continues || top ? "none" : "bottom"}
      // `.section_title-pr`: the published band sets 40 of its own over
      // itself (32 on a phone), inside a container that either has no padding
      // or, where the page names `top`, `.is-top_padding`'s 100 (64, then 48).
      // Both as padding here: a margin on the box below would collapse
      // straight back out of a parent with no padding.
      sx={
        top
          ? {
              pt: "140px",
              [NOT_WIDE]: { pt: "104px" },
              [TINY]: { pt: "80px" },
            }
          : { pt: "40px", [TINY]: { pt: "32px" } }
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "32px",
          mb: "20px",
          [TINY]: { mb: "36px" },
        }}
      >
        <BandHeading block={block} gap="8px" wideRail="70%" />
        <Box
          sx={{
            display: "grid",
            gap: "8px",
            // `is-autofit`: the published grid counts its own columns, and
            // lands on four, two and one at the steps the site turns at.
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          }}
        >
          {block.items.map((card, index) => (
            <PremiumCard key={index} card={card} />
          ))}
        </Box>
      </Box>
    </LandingSection>
  );
}
